import type { Currency } from '../../types/api';

/**
 * Convert USD value to another currency
 * @param usdValue - The USD value to convert
 * @param targetCurrencyCode - The target currency code (e.g., 'EUR', 'GBP', 'USD')
 * @param currencies - Array of currency data from the lookup service
 * @returns The converted value in the target currency, or the original USD value if conversion fails
 */
export function convertUSDToCurrency(
  usdValue: number,
  targetCurrencyCode: string,
  currencies: Currency[]
): number {
  if (usdValue == null || !targetCurrencyCode || !currencies || currencies.length === 0) {
    return usdValue ?? 0;
  }

  // If target is USD, return as-is
  if (targetCurrencyCode.toUpperCase() === 'USD') {
    return usdValue;
  }

  // Find the target currency
  const targetCurrency = currencies.find(
    (currency) => currency.currency?.toUpperCase() === targetCurrencyCode.toUpperCase()
  );

  if (!targetCurrency) {
    console.warn(`Currency ${targetCurrencyCode} not found. Returning USD value.`);
    return usdValue;
  }

  // Use units_per_usd if available (direct conversion: USD * units_per_usd = target currency)
  if (targetCurrency.units_per_usd != null && targetCurrency.units_per_usd > 0) {
    return usdValue * targetCurrency.units_per_usd;
  }

  // Fallback to usd_per_unit (inverse conversion: USD / usd_per_unit = target currency)
  if (targetCurrency.usd_per_unit != null && targetCurrency.usd_per_unit > 0) {
    return usdValue / targetCurrency.usd_per_unit;
  }

  // If no conversion rate available, return original value
  console.warn(
    `No conversion rate available for ${targetCurrencyCode}. Returning USD value.`
  );
  return usdValue;
}

/**
 * Convert USD value to another currency with formatting
 * @param usdValue - The USD value to convert
 * @param targetCurrencyCode - The target currency code (e.g., 'EUR', 'GBP', 'USD')
 * @param currencies - Array of currency data from the lookup service
 * @param options - Optional formatting options
 * @returns Formatted string with the converted value
 */
export function convertUSDToCurrencyFormatted(
  usdValue: number,
  targetCurrencyCode: string,
  currencies: Currency[],
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    useCurrencySymbol?: boolean;
  }
): string {
  const convertedValue = convertUSDToCurrency(usdValue, targetCurrencyCode, currencies);

  if (convertedValue === 0) {
    return '0';
  }

  const targetCurrency = currencies.find(
    (currency) => currency.currency?.toUpperCase() === targetCurrencyCode.toUpperCase()
  );

  const symbol = options?.useCurrencySymbol && targetCurrency?.symbol
    ? `${targetCurrency.symbol} `
    : '';

  const formattedValue = convertedValue.toLocaleString('en-US', {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2
  });

  return `${symbol}${formattedValue}`;
}

/**
 * Get currency symbol for a currency code
 * @param currencyCode - The currency code
 * @param currencies - Array of currency data from the lookup service
 * @returns The currency symbol or the currency code if symbol not found
 */
export function getCurrencySymbol(
  currencyCode: string,
  currencies: Currency[]
): string {
  if (!currencyCode || !currencies || currencies.length === 0) {
    return currencyCode || '';
  }

  const currency = currencies.find(
    (c) => c.currency?.toUpperCase() === currencyCode.toUpperCase()
  );

  return currency?.symbol || currency?.currency || currencyCode;
}

